import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Music,
  Calendar,
  Download,
  Clock,
  FileText,
  Sparkles,
  AlertCircle,
  TrendingUp,
  Album,
  User
} from 'lucide-react';
import { SequenceDetail } from '@/types/sequence';
import { formatDistanceToNow, format } from 'date-fns';
import { formatDuration, formatHolidayName } from '@/utils/formatters';

interface SequenceMetadataProps {
  sequence: SequenceDetail;
}

const SequenceMetadata: React.FC<SequenceMetadataProps> = ({ sequence }) => {
  const song = sequence.song;
  const hasSpotifyData = !!(song?.spotifyTrackId || song?.album || song?.spotifyPopularity);
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Sequence Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Song Information with Album Art */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Song Information
          </h4>
          
          <div className="flex gap-4">
            {(song?.imageUrl || sequence.album_cover_url) && (
              <div className="shrink-0">
                <img 
                  src={song?.imageUrl || sequence.album_cover_url} 
                  alt={`${song?.album || song?.title || sequence.title} cover`}
                  className="w-32 h-32 rounded-md object-cover shadow-xs"
                />
              </div>
            )}
            
            <div className="flex-1 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{song?.title || sequence.title}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {song?.artist || sequence.artist || 'Unknown Artist'}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  {(song?.spotifyTrackId || sequence.spotify_track_id) && (
                    <Badge variant="outline" className="text-xs bg-spotify-green/10 border-spotify-green/30 text-spotify-green shrink-0">
                      <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                      </svg>
                      Spotify
                    </Badge>
                  )}
                  {song?.spotifyExplicit && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Explicit
                    </Badge>
                  )}
                </div>
              </div>
              
              {(song?.album || sequence.album) && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Album className="h-3 w-3" />
                  {song?.album || sequence.album}
                </p>
              )}
              
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDuration(song?.duration || sequence.duration, song?.spotifyDurationMs || sequence.spotify_duration_ms)}
                </span>
                {song?.genre && (
                  <span className="flex items-center gap-1">
                    <Music className="h-3 w-3" />
                    {song.genre}
                  </span>
                )}
                {song?.spotifyPopularity && (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {song.spotifyPopularity}% popular
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Technical Details */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Lighting Nerd Info
          </h4>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Software</p>
              <Badge variant="outline" className="mt-1 border-2">
                {sequence.software}
              </Badge>
            </div>
            
            <div>
              <p className="text-muted-foreground text-xs">Holiday</p>
              <Badge variant="outline" className="mt-1 border-2">
                {formatHolidayName(sequence.holiday)}
              </Badge>
            </div>
            
            {sequence.channelCount && (
              <div>
                <p className="text-muted-foreground text-xs">Channels</p>
                <p className="font-medium">{sequence.channelCount.toLocaleString()}</p>
              </div>
            )}
            
            <div>
              <p className="text-muted-foreground text-xs">Duration</p>
              <p className="font-medium">{formatDuration(sequence.duration, sequence.spotify_duration_ms)}</p>
            </div>
            
            <div>
              <p className="text-muted-foreground text-xs">Files Included</p>
              <p className="font-medium">{sequence.file_type || 'Sequence file'}</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Statistics */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Statistics
          </h4>
          
          <div className="space-y-2 text-sm">
            {sequence.downloadCount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Download className="h-3 w-3" />
                  Downloads
                </span>
                <span className="font-medium">{sequence.downloadCount.toLocaleString()}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Created
              </span>
              <span className="font-medium">
                {format(new Date(sequence.createdAt), 'MMM d, yyyy')}
              </span>
            </div>
            
            {sequence.updatedAt && sequence.updatedAt !== sequence.createdAt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last Updated
                </span>
                <span className="font-medium">
                  {formatDistanceToNow(new Date(sequence.updatedAt), { addSuffix: true })}
                </span>
              </div>
            )}
            
            {sequence.featured && (
              <div className="pt-2">
                <Badge variant="default" className="w-full justify-center">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Featured Sequence
                </Badge>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SequenceMetadata;